import { ReactNode, useState, useMemo, useEffect, Fragment } from "react";
import { Dropdown } from "react-bootstrap";
// components
import InputToggle from "./components/input-toggle";
import NoteCodeMenuItem from "./components/node-code-menu-item";
import ActionsBar from "../actions-bar/ActionsBar";
// utils
import getClasses from "utils/getClasses";
// constants
import messages from "messages";
// styles
import styles from "./NoteCode.module.scss";
// hooks
import { useNoteCodes } from "../../contexts/NoteCodesContext";

type Item = {
  id: string;
  type: string;
  description: string;
  [key: string]: any;
};

type Props<T extends Item> = {
  /**
   * label to show as grouped header in dropdown menu
   */
  labelTypes?: Record<string, string>;
  sortedCodes: T[];
  code: T;
  onUpdate: (arg: { id: string; [key: string]: any }) => void;
  onDelete?: () => void;
  onCreate: () => void;
  placeholder: string;
  children?: ReactNode;
};

const NoteCode = <T extends Item>({
  labelTypes,
  code,
  sortedCodes,
  onCreate,
  onUpdate,
  onDelete,
  placeholder,
  children,
}: Props<T>) => {
  const { readOnly } = useNoteCodes();
  const [search, setSearch] = useState("");

  const codesByType = useMemo(() => {
    return sortedCodes.reduce((prev, curr) => {
      const upperCased = search.toUpperCase();
      if (
        curr.id.toUpperCase().includes(upperCased) ||
        curr.description.toUpperCase().includes(upperCased)
      ) {
        if (!prev[curr.typeLabel || curr.type]) {
          prev[curr.typeLabel || curr.type] = [];
        }

        prev[curr.typeLabel || curr.type].push(curr);
      }

      return prev;
    }, {} as Record<string, typeof sortedCodes>);
  }, [sortedCodes, search]);

  const hasResults = useMemo(
    () => Object.keys(codesByType).length > 0,
    [codesByType],
  );

  const [show, setShow] = useState(false);
  // /**
  //  * disable behavior
  //  */
  // const handleToggle = useCallback(
  //   (nextShow: boolean) => (!readOnly ? null : setShow(nextShow)),
  //   [readOnly],
  // );

  const memoedShow = readOnly ? false : show;

  useEffect(() => {
    if (!show) {
      setSearch("");
    }
  }, [show]);

  return (
    <div className={styles["note-code"]}>
      <div className={styles["note-code__actions"]}>
        <Dropdown show={memoedShow} onToggle={setShow}>
          <Dropdown.Toggle
            readOnly={readOnly}
            className={styles["note-code__toggle"]}
            as={InputToggle}
            code={code}
            search={search}
            show={memoedShow}
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
            placeholder={placeholder}
          />

          <Dropdown.Menu className={styles["note-code__menu"]}>
            {!hasResults ? (
              <Dropdown.ItemText
                className={getClasses(
                  styles["note-code__dropdown-item"],
                  styles["note-code__dropdown-item--empty"],
                )}
              >
                {messages.noResultsFound}
              </Dropdown.ItemText>
            ) : (
              Object.entries(codesByType).map(([type, items]) => (
                <Fragment key={type}>
                  <Dropdown.ItemText
                    className={getClasses(
                      styles["note-code__dropdown-item"],
                      styles["note-code__dropdown-item--header"],
                    )}
                  >
                    {labelTypes?.[type] || type}
                  </Dropdown.ItemText>

                  {items.map((el) => (
                    <NoteCodeMenuItem
                      key={el.id}
                      id={el.id}
                      description={el.description}
                      className={styles["note-code__dropdown-item"]}
                      onClick={() =>
                        onUpdate({
                          id: el.id,
                        })
                      }
                    />
                  ))}
                </Fragment>
              ))
            )}
          </Dropdown.Menu>
        </Dropdown>
        {!readOnly && <ActionsBar onDelete={onDelete} onCreate={onCreate} />}
      </div>

      {children}
    </div>
  );
};

export default NoteCode;
